package com.nonopn.skywatcher.common.app.pages.map.view

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.material.Icon
import androidx.compose.material.ripple
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.skywatcher.common.app.home.HotPreviewApp
import com.nonopn.skywatcher.common.app.pages.map.model.FakeMainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.MainScreenModel
import com.nonopn.skywatcher.common.app.pages.map.model.weather.ProbeStatus
import com.nonopn.skywatcher.common.app.pages.map.model.weather.SortBy
import com.nonopn.skywatcher.common.app.pages.map.model.weather.WeatherReport
import com.nonopn.skywatcher.design.theme.LocalApplicationColorTheme
import com.nonopn.skywatcher.design.utils.localized
import com.nonopn.skywatcher.design.widgets.DefaultCard
import com.nonopn.skywatcher.design.widgets.defaultCardBackground
import com.nonopn.skywatcher.maps.LatLng
import de.drick.compose.hotpreview.HotPreview
import eu.codlab.compose.widgets.TextNormal

@Composable
fun RightColumn(
    modifier: Modifier,
    model: MainScreenModel
) {
    val state by model.states.collectAsState()
    val currentCoordinates = state.currentCoordinates

    if (state.updatingCoordinates) {
        RightColumnLoading(modifier, model)
        return
    }

    if (null == state.probeStatus?.report || null == currentCoordinates) {
        RightColumnError(modifier, model)
        return
    }

    RightColumnLoaded(
        modifier,
        model,
        currentCoordinates
    )
}

@Composable
private fun RightColumnLoading(modifier: Modifier, model: MainScreenModel) {
    RightColumnMessage(modifier, model, "Loading lightning data", false)
}

@Composable
private fun RightColumnError(modifier: Modifier, model: MainScreenModel) {
    RightColumnMessage(modifier, model, "Error loading lightning data", true)
}

@Composable
private fun RightColumnMessage(
    modifier: Modifier,
    model: MainScreenModel,
    message: String,
    canReload: Boolean
) {
    val color = defaultCardBackground()
    val colors = LocalApplicationColorTheme.current

    DefaultCard(
        modifier,
        backgroundColor = color,
        columnModifier = Modifier.padding(12.dp)
    ) {
        Column(
            Modifier.weight(1.0f).fillMaxWidth(),
            verticalArrangement = Arrangement.Center,
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            TextNormal(
                message,
                color = colors.fonts.graySemiTransparent,
                fontSize = 10.sp
            )
        }
    }
}

@Composable
private fun RightColumnLoaded(
    modifier: Modifier,
    model: MainScreenModel,
    position: LatLng,
) {
    val color = defaultCardBackground()
    val state by model.states.collectAsState()
    val lightninActivityCondensed = state.lightninActivityCondensed

    Column(modifier.fillMaxSize().background(color)) {
        val column1Weight = .33f // 33%
        val column2Weight = .33f // 33%
        val column3Weight = .33f // 33%
        LazyColumn(Modifier.fillMaxSize().padding(4.dp)) {
            // Here is the header
            item {
                Row(Modifier.background(Color.Gray)) {
                    val sortBy = state.lightningActivityCondensedSortedBy
                    TableCell(
                        text = "Strength", weight = column1Weight,
                        imageVector = if (sortBy.sortBy == SortBy.Strength) {
                            state.lightningActivityCondensedSortedBy.order.icon
                        } else null
                    ) {
                        model.sortLightningActivityBy(SortBy.Strength)
                    }

                    TableCell(
                        text = "Distance", weight = column2Weight,
                        imageVector = if (sortBy.sortBy == SortBy.Distance) {
                            state.lightningActivityCondensedSortedBy.order.icon
                        } else null
                    ) {
                        model.sortLightningActivityBy(SortBy.Distance)
                    }

                    TableCell(
                        text = "Time", weight = column3Weight,
                        imageVector = if (sortBy.sortBy == SortBy.Time) {
                            state.lightningActivityCondensedSortedBy.order.icon
                        } else null
                    ) {
                        model.sortLightningActivityBy(SortBy.Time)
                    }
                }
            }
            // Here are all the lines of your table.
            items(lightninActivityCondensed.size) { index ->
                val activity = lightninActivityCondensed[index]
                Row(Modifier.fillMaxWidth()) {
                    TableCell(
                        text = activity.intensity,
                        weight = column1Weight
                    )
                    TableCell(
                        text = activity.distance,
                        weight = column2Weight
                    )
                    TableCell(
                        text = "~${activity.createdAtDiff}${activity.createdAtType.stringResource.localized()}",
                        weight = column3Weight
                    )
                }
            }
        }
    }
}

@Composable
fun RowScope.TableCell(
    text: String,
    weight: Float,
    imageVector: ImageVector? = null,
    onClick: (() -> Unit)? = null,
) {
    Row(
        modifier =
            Modifier
                .border(1.dp, Color.Black)
                .weight(weight)
                .let { modifier ->
                    onClick?.let {
                        modifier.clickable(

                            interactionSource = remember { MutableInteractionSource() },
                            indication = ripple(),
                            onClick = onClick
                        )
                    } ?: modifier
                }
                .padding(8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(2.dp)
    ) {
        imageVector?.let {
            Icon(
                modifier = Modifier.size(10.dp),
                imageVector = imageVector,
                contentDescription = ""
            )
        }
        TextNormal(
            text = text,
            fontSize = 12.sp
        )
    }
}

@HotPreview(widthDp = 250, heightDp = 800, darkMode = true)
@HotPreview(widthDp = 250, heightDp = 800, darkMode = false)
@Composable
private fun RightColumnPreview() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme(), isPreview = true) {
        RightColumn(
            Modifier.fillMaxSize(),
            FakeMainScreenModel(
                false,

                fakeProbeStatus = ProbeStatus(
                    report = WeatherReport.fake()
                ),
            )
        )
    }
}

@HotPreview(widthDp = 250, heightDp = 400, darkMode = true)
@HotPreview(widthDp = 250, heightDp = 400, darkMode = false)
@Composable
private fun RightColumnNoForecastPrev() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme(), isPreview = true) {
        RightColumn(Modifier.fillMaxSize(), FakeMainScreenModel(false, null))
    }
}

@HotPreview(widthDp = 250, heightDp = 400, darkMode = true)
@HotPreview(widthDp = 250, heightDp = 400, darkMode = false)
@Composable
private fun MainColumnLoadingForecastPrev() {
    HotPreviewApp(Modifier.fillMaxSize(), isDarkTheme = isSystemInDarkTheme(), isPreview = true) {
        RightColumn(
            Modifier.fillMaxSize(),
            FakeMainScreenModel(
                true,
                null
            )
        )
    }
}

